import { ReactNode, useEffect, useLayoutEffect, useState } from "react";
import styles from "./poster-editor-toolbar.module.css";
import { ToolboxRouteSource } from "./toolboxes/pre-made-toolboxes/route-source/toolbox-route-source";
import { ToolboxMapStyle } from "./toolboxes/pre-made-toolboxes/map-style/toolbox-map-style";
import { ToolboxPosterSize2 } from "./toolboxes/pre-made-toolboxes/poster-size/toolbox-poster-size-2";
import { ToolboxLineStyle } from "./toolboxes/pre-made-toolboxes/line-style/toolbox-line-style";
import { ToolboxBorderStyle } from "./toolboxes/pre-made-toolboxes/border-style/border-style";
import { ToolboxStaticMapLabelStyle } from "./toolboxes/pre-made-toolboxes/labels/toolbox-static-label-style";
import { ToolboxPointStyle } from "./toolboxes/pre-made-toolboxes/point-style/toolbox-point-style";
import { UIIconName } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-type";
import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";
import { filterClasses } from "PosterEditor/utils/filter-classes";
import { useEditorUI, EditorUIState } from "PosterEditor/use-editor-ui";
import { ToolboxUnselectedElement } from "PosterEditor/map/overlay/point-editor/toolbar-unselected-element";
import { ToolboxAllLabels } from "./toolboxes/pre-made-toolboxes/labels/toolbox-all-labels";
import { ToolboxAllMapLabels } from "./toolboxes/pre-made-toolboxes/map-labels/toolbox-all-map-labels";
import { ToolboxAllBackdrops } from "./toolboxes/pre-made-toolboxes/backdrops/toolbox-all-backdrops";
import { ToolboxBackdrop } from "./toolboxes/pre-made-toolboxes/backdrops/toolbox-backdrop";
import { InfoBox } from "BEDROCK/shared-components/info-box";

import { TemplateEditing } from "./toolboxes/pre-made-toolboxes/template-editing/template-editing";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { DraftOptions } from "./toolboxes/pre-made-toolboxes/draft-options/draft-options";
import { PosterProject } from "PosterEditor/model/poster-project";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { ToolboxUploads } from "./toolboxes/pre-made-toolboxes/asset-import/toolbox-uploads";
import { UIIconButton } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { usePaths } from "BEDROCK/paths";
import { AlertModal } from "BEDROCK/shared-components/alert-modal";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { useAnalyticsTracker } from "analytics-tracker";

import { has, set } from "lodash";
import { tab } from "@testing-library/user-event/dist/tab";
import { TooltipTarget, useTooltip } from "PosterEditor/tooltips";
import { MobileDrawer } from "./mobile-drawer";
//   | "templateEditing"
//   | "pointStyle"
//   | "lineStyle"
//   | "mapSize"
//   | "gpsSources"
//   | "mapStyle"
//   | "legend"
//   | "borderStyle";

interface PosterEditorToolbarProps {
  onClickSave: () => void;
  isMobile?: boolean;
  isDrawerExpanded?: boolean;
  onDrawerToggle?: (expanded: boolean) => void;
}

export type ToolboxItemType =
  | "draftOptions"
  | "assetImport"
  | "templateEditing"
  | "pointStyle"
  | "labelStyle"
  | "lineStyle"
  | "mapSize"
  | "gpsSources"
  | "mapStyle"
  | "borderStyle"
  | "backdrops"
  | "mapLabels";

const iconForToolboxType = (type: ToolboxItemType): UIIconName | undefined => {
  switch (type) {
    case "draftOptions":
      return undefined;
    case "assetImport":
      return "IPHONE-UPLOAD";
    case "templateEditing":
      return "TEMPLATES";
    case "pointStyle":
      return "POINT-STYLE";
    case "lineStyle":
      return "LINE-STYLE";
    case "mapSize":
      return "SIZE";
    case "gpsSources":
      return "GPS";
    case "mapStyle":
      return "MAP";
    case "borderStyle":
      return "BORDER";
    case "labelStyle":
      return "STATIC-LABEL";
    case "backdrops":
      return "BACKDROP";
    case "mapLabels":
      return "MAP-LABEL";
  }
};

const shortDescriptionForToolboxType = (type: ToolboxItemType): string => {
  switch (type) {
    case "draftOptions":
      return "Draft";
    case "assetImport":
      return "Add your uploaded images and route";
    case "templateEditing":
      return "Edit the template";
    case "pointStyle":
      return "Style points of your route with icons and colors";
    case "lineStyle":
      return "Style your route line";
    case "mapSize":
      return "Choose a size for your map";
    case "gpsSources":
      return "Upload a GPS file to create a route";
    case "mapStyle":
      return "Choose a color theme for your map";
    case "borderStyle":
      return "Change the border width and color";
    case "labelStyle":
      return "Customise labels on your map";
    case "backdrops":
      return "Add backdrops to provide frames around labels";
    case "mapLabels":
      return "Add text & photo labels";
  }
};

// Map toolbox types to tooltip IDs (only for items with tooltips)
const tooltipIdForToolboxType = (type: ToolboxItemType): string | undefined => {
  switch (type) {
    case "lineStyle":
      return "gps-upload"; // Shows "upload GPX" tooltip on Route Line item
    case "labelStyle":
      return "static-labels"; // Shows "add labels" tooltip on Static Labels item
    default:
      return undefined;
  }
};

const longDescriptionForToolboxType = (type: ToolboxItemType): string => {
  switch (type) {
    case "templateEditing":
      return "Edit the template";
    case "lineStyle":
      return "Add your trail data and style your route line";
    case "mapSize":
      return "Choose a size for your map";
    case "mapStyle":
      return "Choose from a range of map styles to compliment your trail";
    case "borderStyle":
      return "Change the width and color of your border to give your trail some space";
    case "labelStyle":
      return "Click a label to edit. These labels hover over your map-great for titles, info and data about your trail";
    case "backdrops":
      return "Add backdrops to provide frames around labels";
    case "mapLabels":
      return "Click on a route point (or anywhere) to add a text or photo label. These labels stick to you map and point to a location.";
    default:
      return "";
  }
};

export const PosterEditorToolbar = (props: PosterEditorToolbarProps) => {
  const { editorUIState, setEditingElement } = useEditorUI();
  const {
    project,
    template,
    isTemplateEditing,
    existingEditedDraftID,
    saveDraft,
    gpsSource,
  } = usePosterProject();
  const [itemUnderCursor, setItemUnderCursor] = useState<ToolboxItemType>();
  const [isShowingDeleteProjectAlert, setIsShowingDeleteProjectAlert] =
    useState<boolean>(false);
  const { deleteProjectAsync, projectsNotInCheckout } = useProjectStore();
  const { home, auth, mapEditor, navigateTo } = usePaths();
  const { isUserAuthenticated } = useAuthContext();
  const { trackEvent } = useAnalyticsTracker();
  const { setProjectState, setCurrentToolbox, setOnDismissNavigate } = useTooltip();

  const [allowEditing, setAllowEditing] = useState(true);

  const [showDraftOptions, setShowDraftOptions] = useState(false);

  const [selectedType, setSelectedType] = useState<
    ToolboxItemType | undefined
  >();

  // Mobile drawer state - use props if provided, otherwise use local state
  const [localDrawerExpanded, setLocalDrawerExpanded] = useState(false);
  const isDrawerExpanded = props.isDrawerExpanded ?? localDrawerExpanded;
  const setIsDrawerExpanded = props.onDrawerToggle ?? setLocalDrawerExpanded;
  let toolbarItems: ToolboxItemType[];

  const goToAuth = () => {
    const redirectPath = `${mapEditor}/${project.id}`;
    const path = `${auth}?redirect=${encodeURIComponent(
      redirectPath
    )}&startedProject=true`;
    navigateTo(path);
  };

  useEffect(() => {
    const existingDraftID = existingEditedDraftID();
    if (!existingDraftID && project.isTemplateDraft) {
      saveDraft();
    }
  }, []);

  useEffect(() => {
    const existingDraftID = existingEditedDraftID();
    const hasExistingEditedDraft =
      existingDraftID !== undefined &&
      existingDraftID !== "guest_project" &&
      existingDraftID !== project.id;
    setAllowEditing(!hasExistingEditedDraft);
    const draftOptions = hasExistingEditedDraft && !isTemplateEditing;
    setShowDraftOptions(draftOptions);
    const toolbox = draftOptions
      ? "draftOptions"
      : template !== undefined
      ? "templateEditing"
      : "mapSize";
    setSelectedType(toolbox);

    // Auto-expand drawer on mobile when showing draft options (no toolbox buttons to click)
    if (draftOptions && props.isMobile) {
      setIsDrawerExpanded(true);
    }
  }, [project.isTemplateDraft]);

  // Sync project state with tooltip system
  // Use labelUpdateKey as dependency since it changes on every label edit
  // (staticLabels array reference doesn't change reliably on edits)
  useEffect(() => {
    const hasGPSTrack =
      gpsSource !== undefined && (gpsSource.allCoordinates?.length ?? 0) > 0;
    // Check if user has actually edited any static labels
    const hasEditedStaticLabels =
      project.staticLabels?.some((label) => label.isEdited) ?? false;
    const hasMapLabels = (project.styledPoints?.length ?? 0) > 0;

    setProjectState({
      hasGPSTrack,
      hasEditedStaticLabels,
      hasMapLabels,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gpsSource,
    project.labelUpdateKey,
    project.styledPoints,
    setProjectState,
  ]);

  // Sync current toolbox selection with tooltip system
  useEffect(() => {
    setCurrentToolbox(selectedType);
  }, [selectedType, setCurrentToolbox]);

  // Navigate to appropriate toolbox when tooltip is dismissed
  useEffect(() => {
    const handleDismissNavigate = (tooltipId: string) => {
      switch (tooltipId) {
        case "gps-upload":
          setSelectedType("lineStyle");
          break;
        case "static-labels":
          setSelectedType("labelStyle");
          break;
        case "map-labels":
          setSelectedType("mapLabels");
          break;
      }
    };
    setOnDismissNavigate(handleDismissNavigate);
    return () => setOnDismissNavigate(undefined);
  }, [setOnDismissNavigate]);

  if (showDraftOptions) {
    toolbarItems = ["draftOptions"];
  } else {
    toolbarItems = [
      "mapSize",
      "lineStyle",
      "mapStyle",
      "borderStyle",
      "labelStyle",
      "mapLabels",
      "backdrops",
    ];
    // if account permissions is "admin" insert "templateEditing" at the beginning of the array
    if (isTemplateEditing) {
      toolbarItems.unshift("templateEditing");
    }

    if (!isTemplateEditing) {
      if (project.editingPermissions.gpsSource === false) {
        toolbarItems = toolbarItems.filter((item) => item !== "gpsSources");
      }
      if (project.editingPermissions.mapStyle === false) {
        toolbarItems = toolbarItems.filter((item) => item !== "mapStyle");
      }
      if (project.editingPermissions.posterSize === false) {
        toolbarItems = toolbarItems.filter((item) => item !== "mapSize");
      }
      // check if all items in project.editingPermissions.line are false and filter out `lineStyle`
      if (
        Object.values(project.editingPermissions.line).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "lineStyle");
      }
      //likewise for point
      if (
        Object.values(project.editingPermissions.point).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "pointStyle");
      }
      //likewise for border
      if (
        Object.values(project.editingPermissions.border).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "borderStyle");
      }
      //likewise for mapLabel
      if (
        Object.values(project.editingPermissions.mapLabel).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "labelStyle");
      }
      //likewise for staticLabel
      if (
        Object.values(project.editingPermissions.staticLabel).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "labelStyle");
      }
      //likewise for backdrop
      if (
        Object.values(project.editingPermissions.backdrop).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "backdrops");
      }
    }
  }

  useLayoutEffect(() => {
    const element = editorUIState.editingElement;
    if (element?.type === "point") {
      setSelectedType("mapLabels");
    } else if (element?.type === "staticLabel") {
      setSelectedType("labelStyle");
    } else if (element?.type === "mapLabel") {
      setSelectedType("mapLabels");
    } else if (element?.type === "backdrop") {
      setSelectedType("backdrops");
    }
  }, [editorUIState.editingElement]);

  const onClickItem = (item: ToolboxItemType) => {
    trackEvent("Toolbar Tool Selected", {
      tool: item,
      previousTool: selectedType,
    });

    setEditingElement(undefined);
    setSelectedType(item);
  };

  const deleteProjectAndGoHome = async () => {
    trackEvent("Project Deleted", {
      projectId: project.id,
      isTemplateDraft: project.isTemplateDraft,
    });

    const projects = [...projectsNotInCheckout(), project];

    await Promise.all(projects.map((proj) => deleteProjectAsync(proj)));

    navigateTo(home);
  };

  // Filter out complex features on mobile
  const mobileToolbarItems = toolbarItems.filter(
    (item) => item !== "mapLabels" && item !== "backdrops"
  );

  const onClickItemMobile = (item: ToolboxItemType) => {
    onClickItem(item);
    setIsDrawerExpanded(true);
  };

  // Mobile layout
  if (props.isMobile) {
    return (
      <MobileDrawer
        isExpanded={isDrawerExpanded}
        onToggle={setIsDrawerExpanded}
      >
        {/* Horizontal toolbar icons */}
        <div className={styles.mobileToolbarRow}>
          {mobileToolbarItems.map((item, index) => {
            const icon = iconForToolboxType(item);
            if (icon) {
              return (
                <button
                  key={index}
                  className={filterClasses(
                    styles.mobileToolbarItem,
                    selectedType === item ? styles.mobileToolbarItemSelected : undefined
                  )}
                  onClick={() => onClickItemMobile(item)}
                >
                  <UIIcon
                    iconName={icon}
                    variant={selectedType === item ? "SELECTED" : "REGULAR"}
                    size={24}
                  />
                </button>
              );
            }
            return null;
          })}
        </div>

        {/* Expanded content */}
        {isDrawerExpanded && (
          <div className={styles.mobileContent}>
            <PresentedContent type={selectedType} project={project} isMobile />
          </div>
        )}
        <AlertModal
          title="Delete Project"
          message="Are you sure you want to delete this project? This action cannot be undone."
          isShowing={isShowingDeleteProjectAlert}
          onDismiss={() => setIsShowingDeleteProjectAlert(false)}
          onProceed={deleteProjectAndGoHome}
          proceedCTA="DELETE"
          dismissCTA="CANCEL"
        />
      </MobileDrawer>
    );
  }

  // Desktop layout (unchanged)
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div
          className={filterClasses(
            styles.toolbarItemStack,
            project.isTemplateDraft || showDraftOptions
              ? styles.toolbarItemStackHidden
              : undefined
          )}
        >
          {toolbarItems.map((item, index) => {
            const icon = iconForToolboxType(item);
            const tooltipId = tooltipIdForToolboxType(item);
            if (icon) {
              const toolbarItem = (
                <div
                  key={index}
                  onMouseEnter={() => setItemUnderCursor(item)}
                  onMouseLeave={() => setItemUnderCursor(undefined)}
                  className={filterClasses(
                    styles.toolbarItem,
                    selectedType === item
                      ? undefined
                      : styles.toolbarItemHoverable
                  )}
                >
                  <button onClick={() => onClickItem(item)}>
                    <div className={styles.icon}>
                      <UIIcon
                        iconName={icon}
                        variant={selectedType === item ? "SELECTED" : "REGULAR"}
                        size={27}
                      />
                    </div>
                    <div className={styles.itemTextContainer}>
                      <h2 className={styles.itemTitle}>
                        {titleForComponent(project, item)}
                      </h2>
                      <p className={styles.itemDescription}>
                        {shortDescriptionForToolboxType(item)}
                      </p>
                    </div>
                  </button>
                </div>
              );

              // Wrap with TooltipTarget if this item has a tooltip
              if (tooltipId) {
                return (
                  <TooltipTarget key={index} tooltipId={tooltipId}>
                    {toolbarItem}
                  </TooltipTarget>
                );
              }

              return toolbarItem;
            }
          })}
        </div>
        <PresentedContent type={selectedType} project={project} />
      </div>
      {allowEditing && (
        <div className={styles.saveCTAContainer}>
          <UIIconButton
            iconName="BIN"
            variant="REGULAR"
            padding={8}
            onClick={() => {
              setIsShowingDeleteProjectAlert(true);
            }}
          />
          <CTAButton
            title="Save & continue"
            variant="PRIMARY"
            className={styles.cta}
            onClick={() => {
              trackEvent("Project Save Button clicked");
              saveDraft();
              props.onClickSave();
            }}
            disabled={false}
            iconPosition="RIGHT"
            iconName="CHEVRON-RIGHT"
          />
          <AlertModal
            title="Delete Project"
            message="Are you sure you want to delete this project? This action cannot be undone."
            isShowing={isShowingDeleteProjectAlert}
            onDismiss={() => setIsShowingDeleteProjectAlert(false)}
            onProceed={deleteProjectAndGoHome}
            proceedCTA="DELETE"
            dismissCTA="CANCEL"
          />
        </div>
      )}
    </div>
  );
};

const titleForComponent = (
  project: PosterProject,
  type?: ToolboxItemType
): string | undefined => {
  switch (type) {
    case "draftOptions":
      return undefined;
    case "assetImport":
      return "Uploads from iphone app";
    case "templateEditing":
      return "Template";
    case "pointStyle":
      return undefined;
    case "lineStyle":
      return "Route Line";
    case "mapSize":
      return "Map Size";
    case "gpsSources":
      return "GPS Source";
    case "mapStyle":
      return "Map Style";
    case "borderStyle":
      return "Border Style";
    case "labelStyle":
      return "Static labels";
    case "backdrops":
      return "BACKROPS";
    case "mapLabels":
      return "Map Labels";
    default:
      return undefined;
  }
};

interface PresentedContentProps {
  type?: ToolboxItemType;
  project: PosterProject;
  isMobile?: boolean;
}
const PresentedContent = (props: PresentedContentProps) => {
  const { editorUIState } = useEditorUI();
  const componentObject = componentForType(editorUIState, props.type);

  let title = titleForComponent(props.project, props.type);
  const titleClass = props.isMobile ? styles.titleMobile : styles.title;
  return (
    <div className={styles.presentedContent}>
      {title && componentObject.showTitleDescriptor && (
        // <div className={styles.title}>
        //   <h1>{title}</h1>
        // </div>
        <div className={titleClass}>
          <InfoBox
            title={title}
            description={longDescriptionForToolboxType(props.type!)}
            iconName={iconForToolboxType(props.type!)}
            isMobile={props.isMobile}
          />
        </div>
      )}
      {componentObject.component}
    </div>
  );
};

const componentForType = (
  editorState: EditorUIState,
  type?: ToolboxItemType
): { component: ReactNode; showTitleDescriptor: boolean } => {
  switch (type) {
    case "draftOptions":
      return { component: <DraftOptions />, showTitleDescriptor: true };
    case "templateEditing":
      return { component: <TemplateEditing />, showTitleDescriptor: true };
    case "pointStyle":
      const point = editorState.editingElement?.styledPoint;
      const editingElementHasSelectedLabel =
        editorState.editingElement?.mapLabel !== undefined;
      if (!point) {
        return {
          component: (
            <ToolboxUnselectedElement
              title="Customise your project"
              body="Select a tool to tweak your map style, or click anywhere on your map to add a text/photo label"
            />
          ),
          showTitleDescriptor: true,
        };
      }
      return {
        component: (
          <ToolboxPointStyle
            point={point}
            selectedTab={editingElementHasSelectedLabel ? "label" : "point"}
          />
        ),
        showTitleDescriptor: !editingElementHasSelectedLabel,
      };
    case "mapLabels":
      //const label = editorState.editingElement?.mapLabel;
      // if (label) {
      //   return <ToolboxMapLabelStyle label={label} onChangePoint={() => {}} />;
      // }
      const label = editorState.editingElement?.mapLabel;
      if (label) {
        return {
          component: <ToolboxPointStyle label={label} />,
          showTitleDescriptor: false,
        };
      }
      return { component: <ToolboxAllMapLabels />, showTitleDescriptor: true };
    case "labelStyle":
      // const label = editorState.editingElement?.mapLabel;
      // if (label) {
      //   return <ToolboxPointStyle label={label} />;
      // }
      const staticLabel = editorState.editingElement?.staticLabel;
      if (staticLabel) {
        return {
          component: <ToolboxStaticMapLabelStyle label={staticLabel} />,
          showTitleDescriptor: false,
        };
      }
      return { component: <ToolboxAllLabels />, showTitleDescriptor: true };
    case "backdrops":
      const backdrop = editorState.editingElement?.backdrop;
      if (backdrop) {
        return {
          component: <ToolboxBackdrop backdrop={backdrop} />,
          showTitleDescriptor: false,
        };
      }
      return { component: <ToolboxAllBackdrops />, showTitleDescriptor: true };
    case "assetImport":
      return { component: <ToolboxUploads />, showTitleDescriptor: true };
    case "lineStyle":
      return {
        component: (
          <ToolboxLineStyle showTitleBar={false} collapsible={false} />
        ),
        showTitleDescriptor: true,
      };
    case "mapSize":
      return {
        component: (
          <ToolboxPosterSize2 showTitleBar={false} collapsible={false} />
        ),
        showTitleDescriptor: true,
      };
    case "gpsSources":
      return {
        component: (
          <ToolboxRouteSource showTitleBar={false} collapsible={false} />
        ),
        showTitleDescriptor: true,
      };
    case "mapStyle":
      return {
        component: <ToolboxMapStyle showTitleBar={false} collapsible={false} />,
        showTitleDescriptor: true,
      };
    case "borderStyle":
      return {
        component: (
          <ToolboxBorderStyle showTitleBar={false} collapsible={false} />
        ),
        showTitleDescriptor: true,
      };
    default:
      return {
        component: <div>Nothing Selected</div>,
        showTitleDescriptor: true,
      };
  }
};
